import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { generatePhotoVariants } from '@/utils/photoVariantGenerator';
import { debugLogger } from '@/utils/debugLogger';

interface FailedUpload {
  fileName: string;      // ✅ Nome do arquivo apenas
  fileSize: number;      // ✅ Tamanho para debug
  error: string;
  timestamp: number;
  partialVariants: string[]; // Files uploaded before failure (for cleanup)
  attempts: number;
}

interface QueueState {
  croppingQueue: File[];
  uploadQueue: File[];
  failedUploads: FailedUpload[];
  processedFileIds: Set<string>;
}

interface UsePhotoUploadQueueProps {
  userId: string;
  existingPhotos: string[];
  onPhotosChange: (photos: string[]) => void;
  maxPhotos?: number;
}

// Generate unique file ID to prevent duplicates
export const generateFileId = (file: File): string => {
  return `${file.name}-${file.size}-${file.lastModified}`;
};

// Generate unique key for failed upload (used to store File in ref)
const getFailedUploadKey = (failedUpload: FailedUpload): string => {
  return `${failedUpload.fileName}-${failedUpload.timestamp}`;
};

export const usePhotoUploadQueue = ({
  userId,
  existingPhotos,
  onPhotosChange,
  maxPhotos = 6,
}: UsePhotoUploadQueueProps) => {
  const [state, setState] = useState<QueueState>({
    croppingQueue: [],
    uploadQueue: [],
    failedUploads: [],
    processedFileIds: new Set<string>(),
  });
  
  const [currentCroppingIndex, setCurrentCroppingIndex] = useState(0);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track current photos to prevent race conditions
  const currentPhotosRef = useRef<string[]>(existingPhotos);
  
  // Store File objects for retry (separate from FailedUpload interface to prevent Datadog overflow)
  const failedFilesRef = useRef<Map<string, File>>(new Map());

  // Sync currentPhotosRef with existingPhotos
  useEffect(() => {
    currentPhotosRef.current = existingPhotos;
  }, [existingPhotos]);

  // Auto-reset indices and error when all queues are empty
  useEffect(() => {
    if (state.croppingQueue.length === 0 && 
        state.uploadQueue.length === 0 && 
        state.failedUploads.length === 0) {
      debugLogger.debug('PhotoUploadQueue', 'All queues complete, resetting indices');
      setCurrentCroppingIndex(0);
      setCurrentUploadIndex(0);
      setError(null);
      setIsUploading(false);
    }
  }, [state.croppingQueue.length, state.uploadQueue.length, state.failedUploads.length]);

  // Clean up processedFileIds for photos that were removed from context
  useEffect(() => {
    if (
      existingPhotos.length === 0 && 
      state.croppingQueue.length === 0 && 
      state.uploadQueue.length === 0 && 
      state.failedUploads.length === 0 && 
      state.processedFileIds.size > 0
    ) {
      setState(prev => ({
        ...prev,
        processedFileIds: new Set<string>(),
      }));
      debugLogger.debug('PhotoUploadQueue', 'All queues empty, cleared processedFileIds');
    }
  }, [existingPhotos.length, state.croppingQueue.length, state.uploadQueue.length, state.failedUploads.length, state.processedFileIds.size]);

  // Cleanup partial variants on failure
  const cleanupVariants = useCallback(async (variantPaths: string[]) => {
    if (variantPaths.length === 0) return;
    
    debugLogger.debug('PhotoUploadQueue', 'Cleaning up partial variants:', variantPaths);
    
    try {
      const { error } = await supabase.storage
        .from('profile_photos')
        .remove(variantPaths);
      
      if (error) {
        debugLogger.error('PhotoUploadQueue', 'Failed to cleanup variants:', error);
      } else {
        debugLogger.debug('PhotoUploadQueue', 'Successfully cleaned up variants');
      }
    } catch (cleanupError) {
      debugLogger.error('PhotoUploadQueue', 'Cleanup error:', cleanupError);
    }
  }, []);

  // Process next upload in queue with transactional rollback
  const processNextUpload = useCallback(async () => {
    if (state.uploadQueue.length === 0 || isUploading) return;
    
    const fileToUpload = state.uploadQueue[0];
    if (!fileToUpload) return;

    setIsUploading(true);
    const uploadedVariants: string[] = [];
    
    try {
      debugLogger.debug('PhotoUploadQueue', 'Starting transactional upload:', {
        fileName: fileToUpload.name,
        queuePosition: currentUploadIndex + 1,
        totalInQueue: state.uploadQueue.length
      });

      const variants = await generatePhotoVariants(fileToUpload);
      
      // Generate userHash and imageHash ONCE for all variants
      const { generateUserHash, generateSecureImageHash } = await import('@/utils/securePhotoNaming');
      const userHash = await generateUserHash(userId);
      const imageHash = await generateSecureImageHash(Date.now());
      
      debugLogger.debug('PhotoUploadQueue', 'Generated shared hashes for all variants:', {
        userHash,
        imageHash,
        totalVariants: variants.length
      });
      
      // Upload variants SEQUENTIALLY (not Promise.all) for transactional control
      const signedUrls: string[] = [];
      
      for (const variant of variants) {
        // Build path manually using shared imageHash + variant suffix
        const path = `${userHash}/${imageHash}_${variant.type}.jpg`;
        
        const { error: uploadError } = await supabase.storage
          .from('profile_photos')
          .upload(path, variant.file, {
            contentType: 'image/jpeg',
            upsert: false,
          });

        if (uploadError) {
          // ROLLBACK: Cleanup already uploaded variants
          debugLogger.error('PhotoUploadQueue', 'Variant upload failed, rolling back:', uploadError);
          await cleanupVariants(uploadedVariants);
          throw new Error(`Failed to upload variant ${variant.type}: ${uploadError.message}`);
        }
        
        uploadedVariants.push(path);
      }

      // Use PRIMARY RELATIVE PATH (variant 0) for storage in database
      // This path NEVER expires - signed URLs are generated on demand for display
      const primaryPath = uploadedVariants[0]; // e.g., "abc123/def456_0.jpg"
      
      // Check if path already exists (prevent duplicates)
      if (currentPhotosRef.current.includes(primaryPath)) {
        debugLogger.warn('PhotoUploadQueue', 'Duplicate photo detected, skipping');
        setState(prev => ({ ...prev, uploadQueue: prev.uploadQueue.slice(1) }));
        setIsUploading(false);
        return;
      }

      // SUCCESS: Update context with RELATIVE PATH (not signed URL)
      const newPhotos = [...currentPhotosRef.current, primaryPath];
      onPhotosChange(newPhotos);

      // Update state: remove from queue
      setState(prev => ({
        ...prev,
        uploadQueue: prev.uploadQueue.slice(1),
      }));

      setCurrentUploadIndex(prev => prev + 1);
      debugLogger.debug('PhotoUploadQueue', 'Transactional upload successful with relative path:', primaryPath);
      
    } catch (error) {
      debugLogger.error('PhotoUploadQueue', 'Upload error:', error);
      
      // Store File object in ref for retry with unified timestamp
      const timestamp = Date.now();
      const failedUploadKey = `${fileToUpload.name}-${timestamp}`;
      failedFilesRef.current.set(failedUploadKey, fileToUpload);
      
      // Add to failed uploads queue (without File object to prevent Datadog overflow)
      setState(prev => ({
        ...prev,
        uploadQueue: prev.uploadQueue.slice(1),
        failedUploads: [...prev.failedUploads, {
          fileName: fileToUpload.name,
          fileSize: fileToUpload.size,
          error: error?.message || 'Erro desconhecido',
          timestamp: timestamp,
          partialVariants: uploadedVariants,
          attempts: 1,
        }],
      }));
      
      setError('Erro ao enviar foto. Você pode tentar novamente.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsUploading(false);
    }
  }, [state.uploadQueue, userId, onPhotosChange, isUploading, currentUploadIndex, cleanupVariants]);

  // Auto-process uploads when queue has items
  useEffect(() => {
    if (state.uploadQueue.length > 0 && !isUploading) {
      processNextUpload();
    }
  }, [state.uploadQueue.length, isUploading, processNextUpload]);

  // Add files to cropping queue with duplicate prevention
  const enqueueCropping = useCallback((files: File[]) => {
    const availableSlots = maxPhotos - (existingPhotos.length + state.croppingQueue.length);
    
    // Track duplicates within this batch
    const seenInBatch = new Set<string>();
    
    // Filter out already processed files AND duplicates within the same batch
    const newFiles = files.filter(file => {
      const fileId = generateFileId(file);
      
      // Check if already processed globally
      if (state.processedFileIds.has(fileId)) {
        return false;
      }
      
      // Check if duplicate within this batch
      if (seenInBatch.has(fileId)) {
        return false;
      }
      
      seenInBatch.add(fileId);
      return true;
    });

    if (newFiles.length === 0 && files.length > 0) {
      setError('Essas fotos já foram adicionadas.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const filesToProcess = newFiles.slice(0, availableSlots);

    if (filesToProcess.length === 0) {
      setError(`Você já tem ${maxPhotos} fotos no limite.`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    debugLogger.debug('PhotoUploadQueue', 'Enqueuing files for cropping:', {
      count: filesToProcess.length,
      total: files.length,
      filtered: newFiles.length
    });

    setState(prev => {
      const newFileIds = new Set(prev.processedFileIds);
      filesToProcess.forEach(file => {
        newFileIds.add(generateFileId(file));
      });
      
      return {
        ...prev,
        croppingQueue: [...prev.croppingQueue, ...filesToProcess],
        processedFileIds: newFileIds,
      };
    });
    
    setError(null);
  }, [existingPhotos.length, state.croppingQueue.length, state.processedFileIds, maxPhotos]);

  // Move cropped file to upload queue
  const onCropComplete = useCallback((croppedFile: File) => {
    debugLogger.debug('PhotoUploadQueue', 'Crop completed, adding to upload queue');
    
    setState(prev => ({
      ...prev,
      croppingQueue: prev.croppingQueue.slice(1), // Remove first item (just cropped)
      uploadQueue: [...prev.uploadQueue, croppedFile],
    }));

    setCurrentCroppingIndex(prev => prev + 1);
  }, []);

  // Skip current crop (cancel without uploading) with cleanup
  const skipCurrentCrop = useCallback(() => {
    const currentFile = state.croppingQueue[0];
    if (!currentFile) return;
    
    debugLogger.debug('PhotoUploadQueue', 'Skipping current crop and cleaning up');
    
    const fileId = generateFileId(currentFile);
    
    // Check if this file is already in uploadQueue (race condition)
    const isInUploadQueue = state.uploadQueue.some(
      file => generateFileId(file) === fileId
    );
    
    if (isInUploadQueue) {
      debugLogger.warn('PhotoUploadQueue', 'File already in upload queue, canceling upload');
      // Remove from uploadQueue as well
      setState(prev => ({
        ...prev,
        croppingQueue: prev.croppingQueue.slice(1),
        uploadQueue: prev.uploadQueue.filter(f => generateFileId(f) !== fileId),
        processedFileIds: (() => {
          const newIds = new Set(prev.processedFileIds);
          newIds.delete(fileId);
          return newIds;
        })(),
      }));
    } else {
      // Normal skip (file not uploaded yet)
      const newFileIds = new Set(state.processedFileIds);
      newFileIds.delete(fileId); // Allow user to retry this file later
      
      setState(prev => ({
        ...prev,
        croppingQueue: prev.croppingQueue.slice(1),
        processedFileIds: newFileIds,
      }));
    }
  }, [state.croppingQueue, state.uploadQueue, state.processedFileIds]);

  // Retry failed upload with cleanup of partial variants
  const retryFailedUpload = useCallback(async (failedUpload: FailedUpload) => {
    const uploadKey = getFailedUploadKey(failedUpload);
    const fileToRetry = failedFilesRef.current.get(uploadKey);
    
    if (!fileToRetry) {
      debugLogger.error('PhotoUploadQueue', 'File not found for retry:', failedUpload.fileName);
      return;
    }
    
    debugLogger.debug('PhotoUploadQueue', 'Retrying failed upload:', failedUpload.fileName);
    
    // Cleanup partial variants before retry
    if (failedUpload.partialVariants.length > 0) {
      await cleanupVariants(failedUpload.partialVariants);
    }
    
    // Remove from failed queue and ref, add back to upload queue
    failedFilesRef.current.delete(uploadKey);
    setState(prev => ({
      ...prev,
      failedUploads: prev.failedUploads.filter(f => f.timestamp !== failedUpload.timestamp),
      uploadQueue: [...prev.uploadQueue, fileToRetry],
    }));
  }, [cleanupVariants]);

  // Discard failed upload permanently
  const discardFailedUpload = useCallback(async (failedUpload: FailedUpload) => {
    const uploadKey = getFailedUploadKey(failedUpload);
    const fileToDiscard = failedFilesRef.current.get(uploadKey);
    
    debugLogger.debug('PhotoUploadQueue', 'Discarding failed upload:', failedUpload.fileName);
    
    // Cleanup partial variants
    if (failedUpload.partialVariants.length > 0) {
      await cleanupVariants(failedUpload.partialVariants);
    }
    
    // Remove from ref and state
    failedFilesRef.current.delete(uploadKey);
    
    if (fileToDiscard) {
      const fileId = generateFileId(fileToDiscard);
      const newFileIds = new Set(state.processedFileIds);
      newFileIds.delete(fileId);
      
      setState(prev => ({
        ...prev,
        failedUploads: prev.failedUploads.filter(f => f.timestamp !== failedUpload.timestamp),
        processedFileIds: newFileIds,
      }));
    } else {
      setState(prev => ({
        ...prev,
        failedUploads: prev.failedUploads.filter(f => f.timestamp !== failedUpload.timestamp),
      }));
    }
  }, [state.processedFileIds, cleanupVariants]);

  // Reset all queues
  const resetQueues = useCallback(() => {
    setState({
      croppingQueue: [],
      uploadQueue: [],
      failedUploads: [],
      processedFileIds: new Set<string>(),
    });
    setCurrentCroppingIndex(0);
    setCurrentUploadIndex(0);
    setError(null);
  }, []);

  const getCurrentCropFile = useCallback(() => {
    return state.croppingQueue[0] || null;
  }, [state.croppingQueue]);

  const isComplete = state.croppingQueue.length === 0 && 
                     state.uploadQueue.length === 0 && 
                     state.failedUploads.length === 0;

  const hasActiveWork = state.croppingQueue.length > 0 || 
                        state.uploadQueue.length > 0 || 
                        state.failedUploads.length > 0;

  return {
    // Queue state
    croppingQueue: state.croppingQueue,
    uploadQueue: state.uploadQueue,
    failedUploads: state.failedUploads,
    processedFileIds: state.processedFileIds,
    
    // Progress tracking
    currentCroppingIndex,
    currentUploadIndex,
    totalCropping: state.croppingQueue.length,
    totalUploading: state.uploadQueue.length,
    totalFailed: state.failedUploads.length,
    
    // Status flags
    isUploading,
    isComplete,
    hasActiveWork,
    error,
    
    // Actions
    enqueueCropping,
    onCropComplete,
    skipCurrentCrop,
    getCurrentCropFile,
    retryFailedUpload,
    discardFailedUpload,
    resetQueues,
  };
};
